import { type Dot, isDotHammered } from '@hillchart/shared';

interface HillLegendProps {
  /**
   * The dots to legend. Each dot becomes a row showing its
   * `displayOrder` (the same number that appears inside the dot on
   * the chart), the dot's color as a pill background, and the
   * milestone name. We sort by `displayOrder` ascending inside the
   * component so callers can pass the array in any order — the
   * visual ordering is now a property of the dot, not of the array.
   */
  dots: readonly Dot[];
}

/**
 * Number-to-name reference rendered below the chart. Lets users
 * identify each scope without parsing the inline labels around the
 * curve, which can be tight when several dots cluster at the same x.
 *
 * Design notes:
 * - The component is intentionally stateless and event-free; the chart
 *   owns drag and number assignment, and there's no "select a dot from
 *   the legend" interaction (yet — could be a follow-up if users ask).
 * - 3-column grid is a pragmatic choice for ≤ 9 scopes (the realistic
 *   ceiling for a Shape Up project). With more scopes the rows wrap
 *   gracefully thanks to the grid; with fewer, empty slots fill out the
 *   right side without breaking the layout.
 * - Background `slate-50` matches the chart card's neutral palette so
 *   the legend reads as a sub-section of the card rather than a
 *   floating panel.
 * - Pill background uses the dot's literal color via inline `style`.
 *   Tailwind can't generate arbitrary hex classes at build time, and
 *   `DOT_COLORS` (defined in `@hillchart/shared/primitives`) is only
 *   guaranteed to satisfy `hexColorSchema`, not a fixed enum — inline
 *   style is the only reliable path.
 * - The number text fill is white. The chosen palette in `DOT_COLORS`
 *   is calibrated to keep WCAG contrast against white at this size;
 *   that constraint is enforced at the palette source.
 */
export function HillLegend({ dots }: HillLegendProps) {
  // Empty render rather than a "no scopes" placeholder — the parent
  // route already shows that placeholder in the chart card body when
  // `viewedHill.dots.length === 0`, and a second one here would be
  // duplicate noise.
  if (dots.length === 0) return null;

  // Defensive copy + sort by `displayOrder`. The legend reads top-to-
  // bottom as 1, 2, 3, … regardless of how the parent ordered the
  // array — historical snapshots and the live view both pass us their
  // dots in whatever order their backing store happened to return,
  // and we want a consistent visual sequence anyway.
  const orderedDots = [...dots].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <ul className="mt-4 grid list-none grid-cols-3 gap-x-4 gap-y-1.5 rounded-md bg-slate-50 px-3.5 py-2.5">
      {orderedDots.map((dot) => {
        // Mirror the chart's hammered styling so the two surfaces agree:
        // a hammered dot in the chart shows up grey + crossed; the same
        // dot in the legend shows up with a grey pill + a tiny cross
        // indicator. The name stays readable so the `[SH]` tag itself
        // remains visible: that's the canonical signal the team types
        // in GitLab, hiding it would be confusing.
        const hammered = isDotHammered(dot.externalScopeName);
        return (
          // Keyed on `externalScopeId` for the same reason `<Hill />`
          // does: it's the stable business identity across live↔snapshot
          // transitions. Using `dot.id` (a UUID for live, a synthetic
          // composite for snapshots) would break reconciliation when the
          // user toggles the snapshot panel. The legend has no internal
          // state to lose, so this is a defence-in-depth more than a
          // correctness requirement — but matching the chart's keying
          // keeps mental overhead low.
          <li
            key={dot.externalScopeId}
            className="flex items-center gap-2 text-slate-700 text-xs"
            data-hammered={hammered ? 'true' : undefined}
          >
            <span
              aria-hidden="true"
              className="relative inline-flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full font-medium text-[10px] text-white"
              style={{ backgroundColor: hammered ? '#94a3b8' : dot.color }}
            >
              {dot.displayOrder}
              {hammered && (
                // Small SVG cross overlay sized to the pill. Same red
                // shade as the chart, scaled down. `pointer-events-none`
                // because the legend rows are not interactive.
                <svg
                  viewBox="-9 -9 18 18"
                  className="pointer-events-none absolute inset-0"
                  aria-hidden="true"
                  data-testid="hammered-legend-cross"
                >
                  <path
                    d="M-4 -4 L4 4 M-4 4 L4 -4"
                    stroke="#dc2626"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </span>
            {/*
              `truncate` so a particularly long milestone name doesn't
              blow out the column width and shove neighbours around. The
              full name remains accessible via the dot's inline label on
              the chart and via the upstream milestone itself.

              When the sync captured an `externalScopeUrl`, the name becomes
              a direct link to that milestone - the chart itself offers no
              per-dot link surface, so the legend is the only place a user
              can jump from a scope to its upstream page. URL-less dots
              (snapshot replays, legacy Linear rows) stay plain text: an
              anchor without an href is not focusable and would only add a
              misleading affordance.
            */}
            {dot.externalScopeUrl === null ? (
              <span className="truncate">{dot.externalScopeName}</span>
            ) : (
              <a
                href={dot.externalScopeUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="truncate hover:underline"
              >
                {dot.externalScopeName}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
